import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import ReCAPTCHA from 'react-google-recaptcha'
import { progressBarFetch, setOriginalFetch } from 'react-fetch-progressbar'
import { trackPromise } from 'react-promise-tracker'
import { useHeaderScroll } from '../../utils/hooks'
import { sendMail } from '../../utils/api'
import { mailMessages, SHOW_MESSAGE_DURATION } from './constants'
import { FormFields, IDialog } from './Dialog.types'

type KFormFields = keyof FormFields
type MailMessages = (typeof mailMessages)[keyof typeof mailMessages] | null

export const useDialog = (dialog: IDialog) => {
  const [submit, setSubmit] = useState(false)
  const [modalIsOpen, setIsOpen] = useState(false)
  const [thankYouModalIsOpen, setThankYouIsOpen] = useState(false)
  const [captchaKey, setCaptchaKey] = useState(0)
  const { handleSubmit, register, reset, formState, setError, setValue, control, getFieldState } = useForm<FormFields>()
  const { setScroll } = useHeaderScroll('header')

  const [messageToShow, setMessageToShow] = useState<MailMessages>(null)
  useEffect(() => {
    if (messageToShow !== null) {
      setTimeout(() => setMessageToShow(null), SHOW_MESSAGE_DURATION)
    }
  }, [messageToShow])

  const form = useRef<HTMLFormElement>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const hideScroll = () => {
    // if the header menu is open the code below will not work
    if (dialog.headerMenuOpen) return
    setScroll(true)
  }

  const showScroll = () => {
    // if the header menu is open the code below will not work
    if (dialog.headerMenuOpen) return
    setScroll(false)
  }

  const openModal = (setIsOpenMethod: any) => {
    setIsOpenMethod(true)
    hideScroll()
  }

  const closeModal = () => {
    setIsOpen(false)
    setMessageToShow(null)
    reset()

    if (document.activeElement) {
      // @ts-ignore
      document.activeElement.blur()
    }
    setSubmit(false)
    showScroll()
  }

  const closeThankYouModal = () => {
    setThankYouIsOpen(false)
    setMessageToShow(null)
    showScroll()
    // close modal headerMenu
    return dialog.onClickButton?.()
  }

  useEffect(() => {
    if (submit && !formState.isSubmitting) {
      reset()
      setSubmit(false)
    }
  }, [formState.isSubmitting])

  const resetCaptcha = () => {
    setCaptchaKey(captchaKey + 1)
  }

  const onFailMail = (error?: string) => {
    setMessageToShow(error || mailMessages.fail)
    resetCaptcha()
  }

  const onSuccessMail = () => {
    setSubmit(true)
    setMessageToShow(mailMessages.success)
    resetCaptcha()
    closeModal()
    openModal(setThankYouIsOpen)
    setTimeout(() => closeThankYouModal(), 5000)
  }

  const onCompleteMail = () => {
    if (recaptchaRef.current) recaptchaRef.current.reset()
    setValue('recaptcha', '')
    resetCaptcha()
    if (typeof window !== `undefined`) {
      // @ts-ignore
      window.fetch = originalFetch
    }
  }

  const originalFetch = typeof window !== `undefined` ? window?.fetch : ''

  const onSubmit = handleSubmit(async (values: FormFields) => {
    setMessageToShow(null)
    if (!form.current) {
      return onFailMail()
    }

    const connectionInfo = Object.assign({}, values, {
      type: 'eventRegistration',
      subject: `[DSR-PT] Event Registration ${values.event}`,
    } as const)

    const { checkbox, ...lead } = connectionInfo
    try {
      if (typeof window !== `undefined`) {
        setOriginalFetch(window.fetch)
        // @ts-ignore
        window.fetch = progressBarFetch
      }
      const response = await trackPromise(sendMail(lead))
      if (!response.ok) {
        const json = await response.json()
        if (json.fields) {
          return json.fields.map((field: { name: KFormFields; message: string }) =>
            setError(field.name, {
              type: 'manual',
              message: field.message,
            })
          )
        } else {
          return onFailMail(json.message)
        }
      }
      onSuccessMail()
    } catch (error) {
      onFailMail()
    } finally {
      onCompleteMail()
    }
  })

  return {
    messageToShow,
    register,
    formState,
    getFieldState,
    closeModal,
    openModal,
    setIsOpen,
    closeThankYouModal,
    thankYouModalIsOpen,
    modalIsOpen,
    onSubmit,
    setValue,
    setError,
    form,
    recaptchaRef,
    control,
    captchaKey,
  }
}
